(() => {
  const NOTICE_STORAGE_KEY = "vetainGratisGiftsRemoved";
  const AUTO_OPEN_KEY_PREFIX = "freeProductAutoOpened";
  const CHECKOUT_BUSY_ATTR = "data-gratis-gifts-guard";

  let checkChain = Promise.resolve();
  let debounceTimer = null;
  let pendingCart = null;
  let applyingUpdate = false;
  let checkoutPending = false;
  let bypassCheckoutGuard = false;
  let stylesInjected = false;

  function cartRoot() {
    return window.Shopify?.routes?.root || "/";
  }

  function cartUrl() {
    return window.themeVariables?.routes?.cartUrl || `${cartRoot()}cart`;
  }

  function giftTiers() {
    const tiers = window.themeVariables?.cartRewardsGiftTiers;
    return Array.isArray(tiers) ? tiers : [];
  }

  function formatGiftTitles(titles) {
    const list = (titles || []).map((title) => String(title || "").trim()).filter(Boolean);
    const fallbackName = list.length > 1 ? "Gratisprodukte" : "Gratisprodukt";
    return { joined: list.join(", "), fallbackName };
  }

  function messageForReason(reason, titles) {
    const { joined, fallbackName } = formatGiftTitles(titles);
    const strings = window.themeVariables?.strings || {};
    const templates = {
      below_threshold:
        strings.cartGratisGiftsRemovedThreshold ||
        "Geschenk „__TITLES__“ entfernt, da die Mindestanforderungen nicht mehr erreicht sind.",
      not_in_tier:
        strings.cartGratisGiftsRemovedNotInTier ||
        "„__TITLES__“ entfernt, da es nicht mehr in der aktuellen Gratisgeschenk-Aktion ist.",
      quantity: "„__TITLES__“ ist nur einmal pro Bestellung gratis – die Menge wurde auf 1 gesetzt.",
      not_free:
        strings.cartGratisGiftsRemoved ||
        "Geschenk „__TITLES__“ entfernt, da die Mindestanforderungen nicht mehr erreicht sind oder es nicht mehr kostenlos ist.",
    };
    const template = templates[reason] || templates.not_free;
    return template.replace(/__TITLES__/g, joined || fallbackName);
  }

  function noticeMessage(payload) {
    const groups = Array.isArray(payload?.groups) ? payload.groups : null;
    if (groups && groups.length) {
      return groups
        .map((group) => messageForReason(group.reason, group.titles))
        .filter(Boolean)
        .join(" ");
    }
    return messageForReason("not_free", payload?.titles);
  }

  function giftDisplayTitle(item) {
    const raw = String(item.product_title || item.title || "").trim();
    if (!raw) return "";
    return raw.split(" - ")[0].split("(")[0].trim() || raw;
  }

  function itemProperties(item) {
    return item?.properties && typeof item.properties === "object" ? item.properties : {};
  }

  function isProtectedBundleGift(item) {
    const props = itemProperties(item);
    if (props._free_gift || props["_free_gift"]) return true;
    if (props._soeren_bundle || props["_soeren_bundle"]) return true;
    if (props._bundle_unique_id || props["_bundle_unique_id"]) return true;

    const addonKey =
      typeof window.addons_property_label === "string" && window.addons_property_label
        ? window.addons_property_label
        : "_AddonSame";
    if (props[addonKey] != null && props[addonKey] !== "") return true;
    return false;
  }

  function looksLikeGratisGift(item) {
    if (!item || item.gift_card) return false;
    if (isProtectedBundleGift(item)) return false;

    const handle = String(item.handle || "").toLowerCase();
    if (handle.startsWith("gratis-") || handle.startsWith("free-")) return true;

    const title = String(item.product_title || item.title || "").trim();
    return /^(gratis|free)(\b|[\s\-_:])/i.test(title);
  }

  /** Product promotion savings (4 für 3, volume discount) on a line; they don't count against thresholds. */
  function promotionCents(item) {
    const titles = rewardsSettings().promotionTitles || [];
    return (item.line_level_discount_allocations || []).reduce((sum, allocation) => {
      const application = allocation?.discount_application || {};
      const isPromotion = application.type === "automatic" && titles.includes(application.title);
      return isPromotion ? sum + (Number(allocation.amount) || 0) : sum;
    }, 0);
  }

  /** Voucher code (Gutschein) prefix match; same rule as isVoucherCode() in the app's rules.js. */
  function isVoucherCode(application) {
    if (application?.type !== "discount_code") return false;
    const code = String(application.title || "").trim().toUpperCase();
    const prefixes = rewardsSettings().voucherPrefixes || [];
    return Boolean(code) && prefixes.some((p) => {
      const prefix = String(p || "").trim().toUpperCase();
      return prefix && code.startsWith(prefix);
    });
  }

  /** Voucher savings (line and order level); they don't count against thresholds. */
  function voucherCents(cart) {
    const lineLevel = (cart?.items || []).reduce(
      (sum, item) =>
        (item.line_level_discount_allocations || []).reduce(
          (s, allocation) =>
            isVoucherCode(allocation?.discount_application) ? s + (Number(allocation.amount) || 0) : s,
          sum,
        ),
      0,
    );
    return (cart?.cart_level_discount_applications || []).reduce(
      (sum, application) =>
        isVoucherCode(application) ? sum + (Number(application.total_allocated_amount) || 0) : sum,
      lineLevel,
    );
  }

  function itemChargedCents(item) {
    if (typeof item.final_line_price === "number") return item.final_line_price + promotionCents(item);
    if (typeof item.line_price === "number") return item.line_price;
    if (typeof item.discounted_price === "number") {
      return item.discounted_price * (parseInt(item.quantity, 10) || 1);
    }
    return 0;
  }

  function itemMatchesTier(item, tier) {
    const productId = item?.product_id != null ? String(item.product_id) : "";
    const handle = String(item?.handle || "").toLowerCase();
    const ids = Array.isArray(tier?.productIds) ? tier.productIds : [];
    const handles = Array.isArray(tier?.handles) ? tier.handles : [];
    if (productId && ids.some((id) => String(id) === productId)) return true;
    if (handle && handles.some((value) => String(value || "").toLowerCase() === handle)) return true;
    return false;
  }

  function rewardsSettings() {
    return window.themeVariables?.cartRewards || {};
  }

  /**
   * EUR (shop currency) -> cart currency rate. Thresholds are EUR, so a cart in BRL
   * is divided by this before comparing. null = unknown (don't remove anything).
   */
  function presentmentRate(cart) {
    const shopCurrency = rewardsSettings().shopCurrency || "EUR";
    const currency = cart?.currency || window.Shopify?.currency?.active;
    if (!currency || currency === shopCurrency) return 1;
    if (window.Shopify?.currency?.active !== currency) return null;
    const rate = parseFloat(window.Shopify?.currency?.rate);
    return rate > 0 ? rate : null;
  }

  /**
   * Stores the storefront rate on the cart ("BRL:6.1234") so Liquid (progress bar)
   * and the checkout extension can convert the EUR thresholds. Returns the updated
   * cart, or null when nothing changed.
   */
  async function syncFxAttribute(cart) {
    const key = rewardsSettings().fxAttribute || "_vetain_fx";
    const rate = presentmentRate(cart);
    const shopCurrency = rewardsSettings().shopCurrency || "EUR";
    const wanted = rate == null || cart?.currency === shopCurrency ? "" : `${cart.currency}:${rate}`;
    const current = cart?.attributes?.[key] || "";
    if (current === wanted || !(cart?.items || []).length) return null;

    const response = await fetch(`${cartUrl()}/update.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { [key]: wanted } }),
    });
    if (!response.ok) return null;
    return response.json();
  }

  function rewardsExcluded(cart) {
    const handles = (rewardsSettings().excludedHandles || []).map((h) => String(h).toLowerCase());
    return (cart?.items || []).some((item) => handles.includes(String(item.handle || "").toLowerCase()));
  }

  function activeGiftTiers(cart) {
    return rewardsExcluded(cart) ? [] : giftTiers();
  }

  function isTierProduct(item, tiers) {
    return tiers.some((tier) => itemMatchesTier(item, tier));
  }

  function qualifyingSubtotalUnits(cart, tiers, rate) {
    const giftCents = (cart?.items || []).reduce((sum, item) => {
      if (isProtectedBundleGift(item)) return sum;
      return looksLikeGratisGift(item) || isTierProduct(item, tiers) ? sum + itemChargedCents(item) : sum;
    }, 0);
    let subtotal;
    if (typeof cart?.total_price === "number") {
      subtotal =
        cart.total_price +
        (cart.items || []).reduce((sum, item) => sum + promotionCents(item), 0) +
        voucherCents(cart);
    } else if (typeof cart?.items_subtotal_price === "number") {
      subtotal = cart.items_subtotal_price;
    } else {
      subtotal = (cart?.items || []).reduce((sum, item) => sum + itemChargedCents(item), 0);
    }
    return Math.max(0, subtotal - giftCents) / 100 / rate;
  }

  /**
   * Same rules as the vetain-functions validation: each unlocked tier grants one unit
   * of one of its products (lowest tier first). Gratis items without a slot are removed,
   * granted gratis items with quantity > 1 are reduced to 1.
   * Returns [{ key, title, reason, quantity }] where quantity is the target quantity.
   */
  function invalidGratisItems(cart) {
    const rate = presentmentRate(cart);
    if (rate == null) return [];
    const tiers = activeGiftTiers(cart);
    const qualifyingUnits = qualifyingSubtotalUnits(cart, tiers, rate);
    const gratisItems = (cart?.items || []).filter((item) => looksLikeGratisGift(item));
    const granted = new Set();

    [...tiers]
      .sort((a, b) => Number(a.threshold || 0) - Number(b.threshold || 0))
      .forEach((tier) => {
        if (qualifyingUnits + 1e-9 < Number(tier.threshold || 0)) return;
        const match = gratisItems.find((item) => !granted.has(item) && itemMatchesTier(item, tier));
        if (match) granted.add(match);
      });

    const items = [];
    gratisItems.forEach((item) => {
      const key = item.key || item.id;
      if (!key) return;
      if (granted.has(item)) {
        if ((parseInt(item.quantity, 10) || 1) > 1) {
          items.push({ key: String(key), title: giftDisplayTitle(item), reason: "quantity", quantity: 1 });
        }
        return;
      }
      const reason = isTierProduct(item, tiers) ? "below_threshold" : "not_in_tier";
      items.push({ key: String(key), title: giftDisplayTitle(item), reason, quantity: 0 });
    });
    return items;
  }

  function noticeGroups(items) {
    const order = [];
    const byReason = new Map();
    (items || []).forEach((item) => {
      const reason = item.reason || "not_free";
      if (!byReason.has(reason)) {
        byReason.set(reason, []);
        order.push(reason);
      }
      if (item.title) byReason.get(reason).push(item.title);
    });
    return order.map((reason) => ({ reason, titles: byReason.get(reason) }));
  }

  async function fetchCart() {
    const response = await fetch(`${cartRoot()}cart.js`, { credentials: "same-origin" });
    if (!response.ok) throw new Error("Failed to fetch cart.");
    return response.json();
  }

  async function updateCartQuantities(updates) {
    const response = await fetch(`${cartUrl()}/update.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Accept-Language": document.documentElement.lang || window.Shopify?.locale || "",
      },
      body: JSON.stringify({
        updates,
        sections: ["mini-cart"],
      }),
    });
    if (!response.ok) throw new Error("Failed to update cart.");
    return response.json();
  }

  function resetFreeProductAutoOpenFlags() {
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && key.startsWith(AUTO_OPEN_KEY_PREFIX)) keys.push(key);
      }
      keys.forEach((key) => localStorage.removeItem(key));
    } catch (_) {
      /* ignore */
    }
  }

  function markNoticePending(payload) {
    try {
      sessionStorage.setItem(
        NOTICE_STORAGE_KEY,
        JSON.stringify({
          titles: payload?.titles || [],
          groups: payload?.groups || [],
        })
      );
    } catch (_) {
      /* ignore */
    }
  }

  function consumeNoticeFlag() {
    try {
      const raw = sessionStorage.getItem(NOTICE_STORAGE_KEY);
      if (!raw) return null;
      sessionStorage.removeItem(NOTICE_STORAGE_KEY);
      if (raw === "1") return { titles: [] };
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : { titles: [] };
    } catch (_) {
      return null;
    }
  }

  function ensureStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    const style = document.createElement("style");
    style.textContent = `
      html[${CHECKOUT_BUSY_ATTR}="busy"] .checkout-button,
      html[${CHECKOUT_BUSY_ATTR}="busy"] [name="checkout"],
      html[${CHECKOUT_BUSY_ATTR}="busy"] .additional-checkout-buttons {
        pointer-events: none;
        opacity: 0.65;
      }
      [data-gratis-gifts-guard-notice] {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1000000;
        padding: 0.5rem 0.75rem;
        padding-top: max(0.5rem, env(safe-area-inset-top, 0px));
        box-sizing: border-box;
        pointer-events: none;
      }
      [data-gratis-gifts-guard-notice] .ed-discount-link-alert__inner {
        pointer-events: auto;
        max-width: var(--container-max-width, 1600px);
        margin: 0 auto;
      }
      [data-gratis-gifts-guard-notice] .form__banner.banner {
        align-items: center;
        margin: 0;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
        border-radius: 4px;
      }
      [data-gratis-gifts-guard-notice] .ed-discount-link-alert__close {
        flex-shrink: 0;
        margin: 0 0 0 auto;
        padding: 0;
        width: 2.75rem;
        height: 1.75rem;
        border: none;
        background: transparent;
        color: inherit;
        font-size: 20px;
        line-height: 1;
        cursor: pointer;
      }
      [data-gratis-gifts-guard-notice].ed-discount-link-alert--leaving {
        opacity: 0;
        transform: translateY(-0.75rem);
        transition: opacity 0.25s ease, transform 0.25s ease;
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
  }

  function dismissNotice(alertEl) {
    if (!alertEl || !alertEl.parentNode) return;
    alertEl.classList.add("ed-discount-link-alert--leaving");
    window.setTimeout(() => {
      if (alertEl.parentNode) alertEl.remove();
    }, 280);
  }

  function showRemovedNotice(payload) {
    document.querySelectorAll("[data-gratis-gifts-guard-notice]").forEach((el) => el.remove());
    document.querySelectorAll(".gratis-gifts-guard-notice").forEach((el) => el.remove());

    const message = noticeMessage(payload || {});
    const closeLabel = window.themeVariables?.strings?.accessibilityClose || "Close";
    let alertEl = null;

    const tpl = document.getElementById("ed-discount-alert-banner-shell");
    if (tpl?.content) {
      const node = tpl.content.cloneNode(true);
      alertEl = node.querySelector("[data-ed-discount-link-alert]");
      if (alertEl) {
        alertEl.removeAttribute("data-ed-discount-link-alert");
        alertEl.removeAttribute("data-ed-discount-codes");
        alertEl.setAttribute("data-gratis-gifts-guard-notice", "");
        alertEl.setAttribute("role", "status");
        const msg = alertEl.querySelector("[data-ed-discount-alert-msg]");
        if (msg) msg.textContent = message;
        const close = alertEl.querySelector("[data-ed-discount-alert-dismiss]");
        if (close) {
          close.addEventListener("click", () => dismissNotice(alertEl));
        }
      }
    }

    if (!alertEl) {
      ensureStyles();
      alertEl = document.createElement("div");
      alertEl.className = "ed-discount-link-alert";
      alertEl.setAttribute("data-gratis-gifts-guard-notice", "");
      alertEl.setAttribute("role", "status");

      const inner = document.createElement("div");
      inner.className = "ed-discount-link-alert__inner";

      const banner = document.createElement("div");
      banner.className = "form__banner banner banner--error";
      banner.style.margin = "0";

      const content = document.createElement("div");
      content.className = "banner__content";
      const paragraph = document.createElement("p");
      paragraph.style.margin = "0";
      paragraph.textContent = message;
      content.appendChild(paragraph);

      const close = document.createElement("button");
      close.type = "button";
      close.className = "ed-discount-link-alert__close";
      close.setAttribute("aria-label", closeLabel);
      close.textContent = "×";
      close.addEventListener("click", () => dismissNotice(alertEl));

      banner.append(content, close);
      inner.appendChild(banner);
      alertEl.appendChild(inner);
    }

    ensureStyles();
    document.body.appendChild(alertEl);
    window.setTimeout(() => dismissNotice(alertEl), 10000);
  }

  function showNoticeIfNeeded() {
    const payload = consumeNoticeFlag();
    if (payload) showRemovedNotice(payload);
  }

  function setBusy(isBusy) {
    ensureStyles();
    if (isBusy) {
      document.documentElement.setAttribute(CHECKOUT_BUSY_ATTR, "busy");
    } else {
      document.documentElement.removeAttribute(CHECKOUT_BUSY_ATTR);
    }
  }

  function dispatchCartEvents(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:updated", { bubbles: true, detail: { cart } })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: { cart, replacementDelay: 0 },
      })
    );
  }

  async function removeInvalidGratis(cart, options = {}) {
    const invalid = invalidGratisItems(cart);
    if (invalid.length === 0) return cart;

    const updates = {};
    invalid.forEach((item) => {
      updates[item.key] = item.quantity || 0;
    });
    const titles = invalid.map((item) => item.title).filter(Boolean);
    const groups = noticeGroups(invalid);

    applyingUpdate = true;
    setBusy(true);
    try {
      const nextCart = await updateCartQuantities(updates);
      resetFreeProductAutoOpenFlags();
      markNoticePending({ titles, groups });

      if (options.allowReload !== false && !checkoutPending && window.themeVariables?.settings?.pageType === "cart") {
        window.location.reload();
        return nextCart;
      }

      if (!checkoutPending) {
        dispatchCartEvents(nextCart);
        window.setTimeout(showNoticeIfNeeded, 150);
      }
      return nextCart;
    } finally {
      setBusy(false);
      window.setTimeout(() => {
        applyingUpdate = false;
      }, 0);
    }
  }

  function runCheck(cartHint, options = {}) {
    const work = async () => {
      let cart =
        cartHint && Array.isArray(cartHint.items) ? cartHint : await fetchCart();
      // Partial cart payloads may lack attributes/currency -> refetch for the FX sync.
      if (!cart.currency || !cart.attributes) cart = await fetchCart();
      const synced = await syncFxAttribute(cart).catch(() => null);
      if (synced) {
        cart = synced;
        // Re-render cart sections so Liquid picks up the rate.
        if (!checkoutPending) {
          applyingUpdate = true;
          dispatchCartEvents(cart);
          window.setTimeout(() => {
            applyingUpdate = false;
          }, 0);
        }
      }
      return removeInvalidGratis(cart, options);
    };

    const result = checkChain.then(work, work);
    checkChain = result.catch(() => {});
    return result;
  }

  function scheduleCheck(cartHint) {
    if (applyingUpdate || checkoutPending) return;
    if (cartHint && Array.isArray(cartHint.items)) pendingCart = cartHint;
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      const snapshot = pendingCart;
      pendingCart = null;
      void runCheck(snapshot).catch(() => {});
    }, 250);
  }

  async function ensureCleanBeforeCheckout() {
    checkoutPending = true;
    window.clearTimeout(debounceTimer);
    pendingCart = null;
    try {
      return await runCheck(null, { allowReload: false });
    } finally {
      checkoutPending = false;
    }
  }

  function isCheckoutForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.id === "mini-cart-form" || form.id === "main-cart-form") return true;
    return Boolean(form.querySelector('[name="checkout"]'));
  }

  function isCheckoutLink(el) {
    if (!el || el.tagName !== "A") return false;
    const href = el.getAttribute("href") || "";
    return /\/checkout(\?|$)/.test(href);
  }

  document.documentElement.addEventListener("cart:updated", (event) => {
    scheduleCheck(event?.detail?.cart);
  });

  document.documentElement.addEventListener("cart:refresh", (event) => {
    scheduleCheck(event?.detail?.cart);
  });

  document.addEventListener(
    "submit",
    (event) => {
      if (bypassCheckoutGuard) return;
      const form = event.target;
      if (!isCheckoutForm(form)) return;

      event.preventDefault();
      event.stopPropagation();

      void ensureCleanBeforeCheckout()
        .catch(() => {})
        .then(() => {
          bypassCheckoutGuard = true;
          const btn =
            form.querySelector('button[name="checkout"], input[name="checkout"][type="submit"]') ||
            document.querySelector(`button[form="${form.id}"][name="checkout"]`);
          if (btn) {
            btn.click();
          } else {
            HTMLFormElement.prototype.submit.call(form);
          }
          window.setTimeout(() => {
            bypassCheckoutGuard = false;
          }, 0);
        });
    },
    true
  );

  document.addEventListener(
    "click",
    (event) => {
      if (bypassCheckoutGuard) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target.closest?.("a[href]");
      if (!link || !isCheckoutLink(link)) return;

      event.preventDefault();
      event.stopPropagation();

      void ensureCleanBeforeCheckout()
        .catch(() => {})
        .then(() => {
          window.location.href = link.href;
        });
    },
    true
  );

  function start() {
    showNoticeIfNeeded();
    const count = window.themeVariables?.settings?.cartCount;
    if (count === 0) return;
    void runCheck(null).catch(() => {});
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      showNoticeIfNeeded();
      void runCheck(null).catch(() => {});
    }
  });
})();
